import { Section } from './CrimtanOptimizationBreakdown';

const cell = { padding: '7px 10px', textAlign: 'left', borderBottom: '1px solid #e5e7eb' };
const list = (items) => <ul>{items.map((item, i) => <li key={i}>{item}</li>)}</ul>;
const windowText = (window) => window?.start && window?.end ? `${window.start} to ${window.end}` : 'Window unavailable';
const number = (value) => value == null ? 'Unavailable' : Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
const changeValue = (value, field, currency) => {
  if (!value?.available) return 'Unavailable';
  if (field.endsWith('amount_micros') && Number.isFinite(Number(value.value))) {
    return `${currency || ''} ${number(Number(value.value) / 1000000)}`.trim();
  }
  return typeof value.value === 'object' ? 'Recorded' : String(value.value);
};

/** Computed campaign health above the cards; deliberately no recommendation recap. */
export default function CampaignHealthReadout({ readout }) {
  const r = readout;
  return <Section title="Campaign health">
    {r.narrative ? <>
      <p style={{ color: '#6b7280', marginBottom: 6 }}>{windowText(r.window)} · <strong>{r.status}</strong></p>
      <p style={{ lineHeight: 1.6, marginTop: 0 }}>{r.narrative}</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Campaign key stats">
          <thead><tr>{(r.stats || []).filter(s => !['clicks', 'impressions'].includes(s.key)).map(stat =>
            <th style={cell} key={stat.key} scope="col">{stat.label}</th>)}</tr></thead>
          <tbody><tr>{(r.stats || []).filter(s => !['clicks', 'impressions'].includes(s.key)).map(stat =>
            <td style={cell} key={stat.key}><strong>{stat.display}</strong>
              {!r.comparison_note && <small style={{ display: 'block', color: '#6b7280' }}>{stat.change_display}</small>}
            </td>)}</tr></tbody>
        </table>
      </div>
      <p style={{ fontSize: '0.85em', color: '#6b7280' }}>{r.comparison_note || `Compared with ${windowText(r.comparison_window)}.`}</p>
    </> : <>
      <p><strong>{r.status}: {r.headline}</strong></p>
      <p>{windowText(r.window)} · compared with {windowText(r.comparison_window)}</p>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label="Campaign key stats">
          <thead><tr>{['Metric', 'Current period', 'Previous period', 'Change'].map(label => <th style={cell} key={label}>{label}</th>)}</tr></thead>
          <tbody>{(r.stats || []).map(stat => <tr key={stat.key}>
            <th style={cell} scope="row">{stat.label}</th><td style={cell}>{stat.display}</td>
            <td style={cell}>{stat.previous_display}</td><td style={cell}>{stat.change_display}</td>
          </tr>)}</tbody>
        </table>
      </div>
      {!!r.going_well?.length && <><h4>Going well</h4>{list(r.going_well)}</>}
      {!!r.needs_attention?.length && <><h4>Needs attention</h4>{list(r.needs_attention.slice(0, 3).map(item => item.text))}</>}
      {!!r.unknowns?.length && <><h4>What we can’t tell yet</h4>{list(r.unknowns)}</>}
    </>}
    <div aria-label="Campaign health status" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: '#f3f4f6', borderRadius: 6, marginTop: 12 }}>
      <span><strong>Delivery:</strong> {r.health?.delivery}</span><span aria-hidden="true">·</span>
      <span><strong>KPI performance:</strong> {r.health?.kpi}</span><span aria-hidden="true">·</span>
      <span><strong>Measurement:</strong> {r.health?.measurement}</span>
    </div>
    {r.narrative && <details style={{ marginTop: 12 }}>
      <summary>Measurement and supporting stats</summary>
      {!!r.needs_attention?.length && list(r.needs_attention.map(item => item.text))}
      {!!r.unknowns?.length && list(r.unknowns)}
      {!!r.caveats?.length && list(r.caveats)}
      <table aria-label="Supporting campaign stats"><thead><tr>{['Metric', 'Current', 'Previous'].map(label => <th key={label} style={cell}>{label}</th>)}</tr></thead>
        <tbody>{(r.stats || []).map(stat => <tr key={stat.key}><th scope="row" style={cell}>{stat.label}</th><td style={cell}>{stat.display}</td><td style={cell}>{stat.previous_display}</td></tr>)}</tbody>
      </table>
    </details>}
    {!!r.facts?.weekly_trend?.length && <details style={{ marginTop: 12 }}>
      <summary>{r.facts?.lifecycle?.early_baseline ? 'Trend since launch' : 'Last 8 weeks'}</summary>
      {r.facts.lifecycle?.launch_date && <p>Launch date: {r.facts.lifecycle.launch_date}. First observed delivery: {r.facts.lifecycle.first_observed_delivery}.</p>}
      <div style={{ overflowX: 'auto' }}><table aria-label="Campaign weekly trend" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr>{['Week', `Spend (${r.facts.currency || 'currency unavailable'})`,
          ['bid_action', 'verified_campaign_bidding_goals'].includes(r.facts.kpi?.basis) ? `${r.facts.kpi.name} (bid action)` : r.facts.kpi?.name || 'Configured KPI',
          ['bid_action', 'verified_campaign_bidding_goals'].includes(r.facts.kpi?.basis) ? 'Cost per bid conversion' : 'Cost per KPI'].map(label => <th key={label} style={cell}>{label}</th>)}</tr></thead>
        <tbody>{r.facts.weekly_trend.map(week => <tr key={week.start}>
          <th scope="row" style={cell}>{windowText(week)}</th><td style={cell}>{number(week.spend)}</td>
          <td style={cell}>{number(week.kpi)}</td><td style={cell}>{number(week.cost_per_kpi)}</td>
        </tr>)}</tbody>
      </table></div>
    </details>}
    {!!r.recent_changes?.length && <details style={{ marginTop: 12 }}>
      <summary>Recent changes</summary>
      {r.recent_changes.map((event, i) => <div key={i}>
        <p><strong>{event.at} · {event.resource_type || 'Campaign setting'}</strong></p>
        {list((event.changes || []).map(change => `${change.field}: ${changeValue(change.before, change.field, r.facts?.currency)} → ${changeValue(change.after, change.field, r.facts?.currency)}`))}
        <p>{event.interpretation}</p>
      </div>)}
      {r.recent_changes_omitted > 0 && <p>{r.recent_changes_omitted} earlier changes omitted.</p>}
    </details>}
  </Section>;
}
